import * as React from 'react';
import ResultDialog from '../../../../components/dialog-types/ResultDialog';
import { PageBody } from '../../../../components/page-components/PageBody';
import { PageContent } from '../../../../components/page-components/PageContent';
import { PagedList } from '../../../../components/paged-list/PagedList';
import { BillingOnlySearchFilter } from '../../../../models/accounting/billing-only/billing-only-search-filters';
import { SearchOrderSummary } from '../../../../models/ordering/search-order-summary';
import { PagedApiResult } from '../../../../models/utilities/paged-request';
import { AccountingSvc } from '../../../../services/accounting-service';
import { StatusCode } from '../../../../utils/api';
import { PageNames } from '../../../../utils/app-constants';
import { BillingOnlyOrderRow } from '../components/BillingOnlyOrderRow';

export const BillingOnlyScene = () => {

    const [service] = React.useState(new AccountingSvc());
    const [filterParams, setFilterParams] = React.useState(new BillingOnlySearchFilter());

    const [orders, setOrders] = React.useState(() => {
         const initial = new PagedApiResult<SearchOrderSummary>();
         initial.pagination = filterParams.paginationInfo;
         initial.pagination.reusePaging = true;
         return initial;
    });

    /**
     * The invoice is made a moment after it is asked for, so rather than searching again and finding
     * the order still waiting, take it off the list.
     */
    const orderInvoiced = (orderId: string) => {
        const remaining = new PagedApiResult<SearchOrderSummary>(orders.pagination);
        remaining.results = orders.results.filter((x: SearchOrderSummary) => x.id !== orderId);
        if (remaining.pagination.totalItems) {
            remaining.pagination.totalItems -= 1;
        }

        setOrders(remaining);
    };

    /**
     * Confirm and invoice the order
     * @param order The order to invoice
     */
    const invoiceOrder = async (order: SearchOrderSummary) => {

        const confirmed = await ResultDialog.show({
            title: 'Invoice Order?',
            message: `Are you sure you want to invoice order ${ order.id }?`
        });

        if (confirmed) {
            const result = await service.invoiceBillingOnlyOrder(order.id);
            if (result.status === StatusCode.Success) {
                orderInvoiced(order.id);
            }
        }
    };

    /**
     * OnSearch handler
     * @param filters The user search filters to apply
     */
    const onSearch = async (filters: BillingOnlySearchFilter) => {
        const response = await service.getBillingOnlyOrders(filters);
        if (response.status === StatusCode.Success) {
            setOrders(response.data);
            scrollTo();
        }

        setFilterParams(filters);
    };

    const onParamsUpdate = (itm : any) => {
        setFilterParams(itm);
    };

    const onParamsReset = () => {
        setFilterParams(new BillingOnlySearchFilter());
    };

    return <PageContent pageName={PageNames.ACCOUNTING_BILLING_ONLY}>
        <PageBody>
            <PagedList
                noResultsMessage="No billing only orders are waiting to be invoiced."
                searchCallback={ onSearch }
                data={ orders }
                filterProps={ filterParams }
                selectableClass=""
                hideFilterBar={ true }
                onFilterUpdate={ onParamsUpdate }
                onReset={ onParamsReset }
                rowRenderer={(order: SearchOrderSummary) => 
                    <BillingOnlyOrderRow
                        key={ order.id }
                        order={ order }          
                        onInvoice={ invoiceOrder }
                    />
                }
            /> 
        </PageBody>
    </PageContent>;
};
